import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Download, Upload, Database, HardDrive, AlertTriangle, Users, Plus, Trash2, Shield, User, Activity, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, KeyRound, Calendar, Send, CheckSquare, Square, Folder } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { useDatasets } from "../context/DatasetContext";
import { useAuth } from "../context/AuthContext";
import { authHeaders } from "../utils/api";
import PartyAuditPanel from "../components/PartyAuditPanel";
import AccessRequestsPanel from "../components/AccessRequestsPanel";
import SmtpSettingsPanel from "../components/SmtpSettingsPanel";
import { usePersistedState } from "../hooks/usePersistedState";

function SystemVersionBadge() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    axios.get(`${API}/system/version`)
      .then((r) => setInfo(r.data))
      .catch(() => {});
  }, []);
  if (!info) return null;
  const boot = info.boot_time ? new Date(info.boot_time).toLocaleString() : "?";
  return (
    <p className="text-[11px] text-muted-foreground font-mono" data-testid="system-version">
      cache: {info.cache_version} &middot; deployed: {boot}
    </p>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UserManagementPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = usePersistedState("ui.settings.userMgmt.collapsed", false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data);
    } catch (e) {
      console.error("Failed to fetch users:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage(null);
    try {
      await axios.post(`${API}/users`, {
        email: newEmail, password: newPassword, name: newName, role: newRole,
      });
      setNewEmail(""); setNewPassword(""); setNewName(""); setNewRole("user");
      setCreating(false);
      setMessage({ type: "success", text: "User created" });
      fetchUsers();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to create user" });
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      setMessage({ type: "success", text: "User deleted" });
      fetchUsers();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to delete user" });
    }
  };

  const handleResetPassword = async (userId, email) => {
    const newPassword = window.prompt(`Enter new password for ${email}:`);
    if (!newPassword) return;
    if (newPassword.length < 4) {
      setMessage({ type: "error", text: "Password must be at least 4 characters" });
      return;
    }
    try {
      await axios.patch(`${API}/users/${userId}`, { password: newPassword });
      setMessage({ type: "success", text: `Password reset for ${email}` });
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to reset password" });
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/users/${userId}`, { role: newRole });
      fetchUsers();
    } catch (e) {
      console.error("Failed to update role:", e);
    }
  };

  return (
    <Card>
      <CardHeader className="cursor-pointer select-none" onClick={() => setCollapsed(!collapsed)} data-testid="user-mgmt-toggle">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-4 h-4" /> User Management
            {collapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronUp className="w-4 h-4 text-muted-foreground" />}
          </CardTitle>
          {!collapsed && (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); setCreating(!creating); }}
              data-testid="create-user-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add User
            </Button>
          )}
        </div>
      </CardHeader>
      {!collapsed && <CardContent className="space-y-4">
        {message && (
          <div className={`px-3 py-2 rounded text-sm ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
            {message.text}
          </div>
        )}

        {creating && (
          <form onSubmit={handleCreate} className="p-4 border rounded-lg space-y-3 bg-muted/30" data-testid="create-user-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Email</label>
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="user@email.com" required className="mt-1" data-testid="new-user-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Password</label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Password" required className="mt-1" data-testid="new-user-password" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Name</label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Display name" className="mt-1" data-testid="new-user-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="new-user-role"
                >
                  <option value="user">User</option>
                  <option value="viewer">Viewer</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" data-testid="submit-create-user">Create User</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading users...</p>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-4 py-2.5 font-medium">User</th>
                  <th className="text-left px-4 py-2.5 font-medium">Role</th>
                  <th className="text-left px-4 py-2.5 font-medium">Created</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-muted/30" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.name || u.email}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2 text-xs"
                        data-testid={`role-select-${u.id}`}
                      >
                        <option value="user">User</option>
                        <option value="viewer">Viewer</option>
                        <option value="superadmin">Superadmin</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          onClick={() => handleResetPassword(u.id, u.email)}
                          title="Reset password"
                          data-testid={`reset-password-${u.id}`}
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-500 hover:text-red-600"
                          onClick={() => handleDelete(u.id, u.email)}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>}
    </Card>
  );
}

function ActivityLogPanel() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = usePersistedState("ui.settings.activityLog.collapsed", false);
  const PAGE_SIZE = 20;

  const fetchLog = async (offset = 0) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/activity-log?limit=${PAGE_SIZE}&offset=${offset}`);
      setEntries(data.entries);
      setTotal(data.total);
    } catch (e) {
      console.error("Failed to fetch activity log:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(page * PAGE_SIZE); }, [page]);

  const actionLabels = {
    login: "Logged in",
    login_failed: "Failed login",
    dataset_uploaded: "Uploaded dataset",
    dataset_deleted: "Deleted dataset",
    user_created: "Created user",
    user_deleted: "Deleted user",
    backup_created: "Downloaded backup",
  };

  const actionColors = {
    login: "bg-emerald-100 text-emerald-800",
    login_failed: "bg-red-100 text-red-800",
    dataset_uploaded: "bg-blue-100 text-blue-800",
    dataset_deleted: "bg-orange-100 text-orange-800",
    user_created: "bg-violet-100 text-violet-800",
    user_deleted: "bg-red-100 text-red-800",
    backup_created: "bg-amber-100 text-amber-800",
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Card>
      <CardHeader className="cursor-pointer select-none" onClick={() => setCollapsed(!collapsed)} data-testid="activity-log-toggle">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-4 h-4" /> Activity Log
          <span className="text-xs font-normal text-muted-foreground ml-1">({total} events)</span>
          {collapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronUp className="w-4 h-4 text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      {!collapsed && <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading activity...</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
        ) : (
          <>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="activity-log-table">
                <thead className="bg-muted/50">
                  <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-2.5 font-medium">Time</th>
                    <th className="text-left px-4 py-2.5 font-medium">Action</th>
                    <th className="text-left px-4 py-2.5 font-medium">User</th>
                    <th className="text-left px-4 py-2.5 font-medium">Details</th>
                    <th className="text-left px-4 py-2.5 font-medium">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {entries.map((e) => (
                    <tr key={e.id} className="hover:bg-muted/30">
                      <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(e.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionColors[e.action] || "bg-gray-100 text-gray-800"}`}>
                          {actionLabels[e.action] || e.action}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs">{e.email || '-'}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[300px] truncate">{e.details || '-'}</td>
                      <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{e.ip_address || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3">
                <p className="text-xs text-muted-foreground">Page {page + 1} of {totalPages}</p>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-7" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-7" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>}
    </Card>
  );
}


function DisplayTimePanel() {
  const { displayTimeRange, fetchAppSettings } = useDatasets();
  const [startYear, setStartYear] = useState(displayTimeRange.start);
  const [endYear, setEndYear] = useState(displayTimeRange.end);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [collapsed, setCollapsed] = usePersistedState("ui.settings.displayTime.collapsed", false);

  useEffect(() => {
    setStartYear(displayTimeRange.start);
    setEndYear(displayTimeRange.end);
  }, [displayTimeRange]);

  const hasChanges = startYear !== displayTimeRange.start || endYear !== displayTimeRange.end;

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await axios.put(`${API}/settings/app`, {
        display_year_start: startYear,
        display_year_end: endYear,
      }, { headers: authHeaders() });
      await fetchAppSettings();
      setMsg({ type: "success", text: "Saved" });
      setTimeout(() => setMsg(null), 2000);
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.detail || "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const years = Array.from({ length: 2030 - 1990 + 1 }, (_, i) => 1990 + i);

  return (
    <Card data-testid="display-time-panel">
      <CardHeader className="cursor-pointer select-none" onClick={() => setCollapsed(!collapsed)}>
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Display Time Range
          <span className="text-xs font-normal text-muted-foreground ml-1">({displayTimeRange.start} – {displayTimeRange.end})</span>
          {collapsed ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronUp className="w-4 h-4 text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      {!collapsed && (
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Controls the year range displayed on all charts and graphs across the dashboard, map, and donor profiles.
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">From</label>
              <select
                value={startYear}
                onChange={(e) => setStartYear(Number(e.target.value))}
                className="h-9 rounded-sm border border-input bg-background px-3 text-sm"
                data-testid="display-year-start"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">To</label>
              <select
                value={endYear}
                onChange={(e) => setEndYear(Number(e.target.value))}
                className="h-9 rounded-sm border border-input bg-background px-3 text-sm"
                data-testid="display-year-end"
              >
                {years.filter(y => y >= startYear).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !hasChanges || endYear < startYear}
              data-testid="save-display-time"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            {msg && (
              <span className={`text-xs ${msg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {msg.text}
              </span>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}


function PushToProductionPanel() {
  const { datasets, folders } = useDatasets();
  const [selected, setSelected] = useState([]);
  const [remoteUrl, setRemoteUrl] = useState("");
  const [pushing, setPushing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [pushStep, setPushStep] = useState(0); // 0=idle, 1=backup, 2=upload, 3=merging, 4=done

  const toggleDs = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleFolder = (folderId) => {
    const ids = datasets.filter(d => d.folder_id === folderId).map(d => d.id);
    const allIn = ids.every(id => selected.includes(id));
    setSelected(prev => allIn ? prev.filter(x => !ids.includes(x)) : [...new Set([...prev, ...ids])]);
  };

  const totalRecords = selected.reduce((sum, id) => {
    const ds = datasets.find(d => d.id === id);
    return sum + (ds?.record_count || 0);
  }, 0);

  const handlePush = async () => {
    if (!selected.length || !remoteUrl) return;
    if (!window.confirm(`Push ${selected.length} dataset(s) (${totalRecords.toLocaleString()} donations) to production? This will MERGE into the remote database.`)) return;
    setPushing(true);
    setPushStep(1);
    setMsg({ type: "success", text: "Starting push..." });
    try {
      const res = await axios.post(`${API}/backup/push-datasets`, null, {
        params: { remote_url: remoteUrl, dataset_ids: selected.join(","), source_url: API },
      });
      if (res.data.status === "already_running") {
        setMsg({ type: "success", text: "Push already in progress..." });
      }
      // Poll for completion
      let done = false;
      let idleCount = 0;
      while (!done) {
        await new Promise(r => setTimeout(r, 2000));
        const st = await axios.get(`${API}/backup/push-datasets/status`);
        const { status, progress, error } = st.data;
        // Determine step from progress text
        if (progress?.includes("Backing up")) setPushStep(1);
        else if (progress?.includes("Uploading")) setPushStep(2);
        else if (progress?.includes("Production") || progress?.includes("Restoring")) setPushStep(3);
        else if (progress?.includes("Done")) setPushStep(4);

        if (status === "done") {
          done = true;
          setPushStep(4);
          setMsg({ type: "success", text: progress });
        } else if (status === "error") {
          done = true;
          setPushStep(0);
          setMsg({ type: "error", text: error });
        } else if (status === "idle") {
          idleCount++;
          if (idleCount > 3) {
            done = true;
            setPushStep(0);
            setMsg({ type: "error", text: "Push status lost — the server may have restarted. Please try again." });
          }
        } else {
          idleCount = 0;
          setMsg({ type: "success", text: progress || "Processing..." });
        }
      }
    } catch (e) {
      setPushStep(0);
      const detail = e.response?.data?.detail || e.response?.data?.error || e.message || "Push failed";
      setMsg({ type: "error", text: detail });
    } finally {
      setPushing(false);
    }
  };

  const handleCancelPush = async () => {
    try {
      await axios.post(`${API}/backup/push-datasets/reset`, null, { headers: authHeaders() });
      setMsg({ type: "success", text: "Push cancelled." });
    } catch (e) {
      setMsg({ type: "error", text: "Failed to cancel — try refreshing the page." });
    } finally {
      setPushing(false);
      setPushStep(0);
    }
  };

  // Group datasets by folder
  const folderMap = {};
  const ungrouped = [];
  datasets.forEach(ds => {
    if (ds.folder_id) {
      if (!folderMap[ds.folder_id]) folderMap[ds.folder_id] = [];
      folderMap[ds.folder_id].push(ds);
    } else {
      ungrouped.push(ds);
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Send className="w-4 h-4" /> Push Datasets to Production
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Select datasets to push to production. This will <strong>merge</strong> into the remote database — existing data for these datasets will be replaced, other data stays untouched.
        </p>

        {msg && (
          <div className={`px-3 py-2 rounded text-sm flex items-center gap-2 ${msg.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`} data-testid="push-message">
            {pushing && msg.type === "success" && <div className="w-3.5 h-3.5 border-2 border-green-600 border-t-transparent rounded-full animate-spin shrink-0" />}
            {msg.text || "Processing..."}
          </div>
        )}

        {/* Step progress bar */}
        {pushing && (
          <div className="space-y-2" data-testid="push-progress">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              {["Creating Backup", "Uploading", "Merging on Prod", "Complete"].map((label, i) => {
                const step = i + 1;
                const active = pushStep === step;
                const done = pushStep > step;
                return (
                  <div key={label} className={`flex items-center gap-1 ${done ? "text-green-600 font-medium" : active ? "text-blue-600 font-semibold" : ""}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] border-2 shrink-0
                      ${done ? "bg-green-600 text-white border-green-600" : active ? "border-blue-600 text-blue-600" : "border-muted-foreground/30"}`}>
                      {done ? "\u2713" : step}
                    </div>
                    <span className="hidden sm:inline">{label}</span>
                  </div>
                );
              })}
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${Math.max(5, (pushStep / 4) * 100)}%` }} />
            </div>
          </div>
        )}

        {/* Dataset selector */}
        <div className="border rounded-lg max-h-60 overflow-y-auto p-3 space-y-2" data-testid="push-dataset-list">
          <div className="flex gap-2 mb-2">
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setSelected(datasets.map(d => d.id))} data-testid="push-select-all">
              Select All
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setSelected([])} data-testid="push-select-none">
              None
            </Button>
          </div>

          {folders.map(folder => {
            const fDs = datasets.filter(d => d.folder_id === folder.id);
            if (!fDs.length) return null;
            const allSel = fDs.every(d => selected.includes(d.id));
            const someSel = fDs.some(d => selected.includes(d.id));
            return (
              <div key={folder.id} className="space-y-1">
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleFolder(folder.id)}>
                  <Checkbox checked={allSel} className="h-3.5 w-3.5" data-testid={`push-folder-${folder.id}`} />
                  <Folder className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs font-semibold">{folder.name}</span>
                  <span className="text-[10px] text-muted-foreground">({fDs.length})</span>
                </div>
                <div className="ml-6 space-y-0.5">
                  {fDs.map(ds => (
                    <div key={ds.id} className="flex items-center gap-2 cursor-pointer" onClick={() => toggleDs(ds.id)}>
                      <Checkbox checked={selected.includes(ds.id)} className="h-3 w-3" data-testid={`push-ds-${ds.id}`} />
                      <span className="text-xs truncate">{ds.name || ds.filename}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{ds.record_count?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {ungrouped.map(ds => (
            <div key={ds.id} className="flex items-center gap-2 cursor-pointer" onClick={() => toggleDs(ds.id)}>
              <Checkbox checked={selected.includes(ds.id)} className="h-3 w-3" data-testid={`push-ds-${ds.id}`} />
              <span className="text-xs truncate">{ds.name || ds.filename}</span>
              <span className="text-[10px] text-muted-foreground ml-auto">{ds.record_count?.toLocaleString()}</span>
            </div>
          ))}
        </div>

        {selected.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {selected.length} dataset(s) selected — {totalRecords.toLocaleString()} donation records
          </p>
        )}

        {/* Remote URL */}
        <div className="space-y-1">
          <label className="text-sm font-medium">
            Production URL <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="https://your-app.emergent.host (base URL only, no path)"
            value={remoteUrl}
            onChange={e => setRemoteUrl(e.target.value)}
            data-testid="push-remote-url"
          />
          {!remoteUrl && (
            <p className="text-xs text-amber-700">
              Required — paste your production base URL here before pushing.
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handlePush}
            disabled={pushing || !selected.length || !remoteUrl}
            className="flex-1"
            data-testid="push-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {pushing ? "Pushing..." : `Push ${selected.length} Dataset(s) to Production`}
          </Button>
          {pushing && (
            <Button
              onClick={handleCancelPush}
              variant="destructive"
              data-testid="push-cancel-btn"
            >
              Cancel
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}



export default function SettingsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState(null);
  const { fetchDatasets } = useDatasets();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/backup/stats`);
      setStats(res.data);
    } catch (e) {
      console.error("Failed to load stats:", e);
    }
  };

  const handleBackup = async () => {
    setLoading(true);
    setMessage(null);
    try {
      // Start backup
      await axios.post(`${API}/backup/start`);
      setMessage({ type: "success", text: "Backup started... preparing data..." });

      // Poll for completion
      let ready = false;
      while (!ready) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await axios.get(`${API}/backup/status`);
        const { status, progress, error } = statusRes.data;
        if (status === "done") {
          ready = true;
          setMessage({ type: "success", text: progress + " — downloading..." });
        } else if (status === "error") {
          setMessage({ type: "error", text: error || "Backup failed" });
          setLoading(false);
          return;
        } else {
          setMessage({ type: "success", text: progress });
        }
      }

      // Download the completed file
      const res = await fetch(`${API}/backup/download`, { headers: authHeaders() });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fec-backup-${new Date().toISOString().slice(0, 10)}.ndjson.gz`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage({ type: "success", text: "Backup downloaded successfully!" });
    } catch (e) {
      setMessage({ type: "error", text: "Failed to create backup" });
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!window.confirm(
      "This will REPLACE all current data with the backup. Are you sure?"
    )) {
      e.target.value = "";
      return;
    }

    setRestoring(true);
    setMessage(null);
    try {
      const CHUNK_SIZE = 4 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      if (totalChunks <= 1) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/restore`, formData, { timeout: 600000 });
        const restored = res.data.restored;
        const total = Object.values(restored).filter(v => typeof v === 'number').reduce((a, b) => a + b, 0);
        setMessage({ type: "success", text: `Restored ${total.toLocaleString()} records across ${Object.keys(restored).length} collections` });
      } else {
        setMessage({ type: "success", text: `Uploading ${totalChunks} chunks (${(file.size / 1024 / 1024).toFixed(1)} MB)...` });
        const initRes = await axios.post(`${API}/restore/chunk-init?total_chunks=${totalChunks}&filename=${encodeURIComponent(file.name)}`);
        const sessionId = initRes.data.session_id;

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const chunk = file.slice(start, start + CHUNK_SIZE);
          const formData = new FormData();
          formData.append("file", chunk);
          await axios.post(`${API}/restore/chunk/${sessionId}/${i}`, formData, { timeout: 120000 });
          setMessage({ type: "success", text: `Uploading chunk ${i + 1}/${totalChunks}...` });
        }

        setMessage({ type: "success", text: "Processing backup..." });
        const res = await axios.post(`${API}/restore/chunk-complete/${sessionId}`, null, { timeout: 600000 });
        const restored = res.data.restored;
        const total = Object.values(restored).filter(v => typeof v === 'number').reduce((a, b) => a + b, 0);
        setMessage({ type: "success", text: `Restored ${total.toLocaleString()} records across ${Object.keys(restored).length} collections` });
      }
      loadStats();
      fetchDatasets();
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.detail || "Failed to restore. For large files, try 'Restore from URL' instead.",
      });
    } finally {
      setRestoring(false);
      e.target.value = "";
    }
  };

  const handleRestoreFromUrl = async () => {
    const url = window.prompt(
      "Enter the production URL (e.g., https://your-app.emergentagent.com):"
    );
    if (!url) return;
    if (!window.confirm("This will REPLACE all current data with the production backup. This may take several minutes. Are you sure?")) return;

    setRestoring(true);
    setMessage({ type: "success", text: "Starting pull from production..." });
    try {
      // Start the pull
      const startRes = await axios.post(`${API}/restore/pull-remote?remote_url=${encodeURIComponent(url)}`);
      if (startRes.data.status === "already_running") {
        setMessage({ type: "success", text: "Pull already in progress..." });
      }

      // Poll for completion
      let done = false;
      while (!done) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await axios.get(`${API}/restore/pull-remote/status`);
        const { status, progress, error } = statusRes.data;
        if (status === "done") {
          done = true;
          setMessage({ type: "success", text: progress + " Please log out and log back in." });
        } else if (status === "error") {
          done = true;
          setMessage({ type: "error", text: error });
        } else {
          setMessage({ type: "success", text: progress });
        }
      }
      loadStats();
      fetchDatasets();
    } catch (e) {
      console.error("Pull remote error:", e);
      let errorText = "Failed to start pull.";
      if (e.response) {
        // Server responded with an error
        const detail = e.response.data?.detail;
        const status = e.response.status;
        if (detail) {
          errorText = detail;
        } else if (status === 404) {
          errorText = "The pull-remote endpoint was not found. Make sure Production has the latest code deployed.";
        } else if (status === 422) {
          errorText = "Invalid URL format. Please enter a valid HTTPS URL.";
        } else if (status === 500) {
          errorText = `Server error (HTTP ${status}). Check the Production server logs.`;
        } else {
          errorText = `HTTP ${status}: ${e.response.statusText || 'Unknown error'}`;
        }
      } else if (e.code === "ERR_NETWORK" || !e.response) {
        errorText = "Network error — could not reach the server. Is the app running?";
      }
      setMessage({ type: "error", text: errorText });
    } finally {
      setRestoring(false);
    }
  };

  const collections = [
    { key: "datasets", label: "Datasets", icon: Database },
    { key: "folders", label: "Folders", icon: HardDrive },
    { key: "donations", label: "Donations", icon: Database },
    { key: "anomalies", label: "Anomalies", icon: AlertTriangle },
    { key: "address_geocode", label: "Geocode Cache", icon: Database },
  ];

  return (
    <div className="space-y-6 p-1" data-testid="settings-page">
      <h1 className="text-2xl font-bold tracking-tight">Settings</h1>

      {message && (
        <div
          className={`px-4 py-3 rounded text-sm ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
          data-testid="settings-message"
        >
          {message.text}
        </div>
      )}

      {/* User Management — superadmin only */}
      {user?.role === "superadmin" && <UserManagementPanel />}

      {/* Access Requests — superadmin only */}
      {user?.role === "superadmin" && <AccessRequestsPanel />}

      {/* SMTP Settings — superadmin only */}
      {user?.role === "superadmin" && <SmtpSettingsPanel />}

      {/* Activity Log — superadmin only */}
      {user?.role === "superadmin" && <ActivityLogPanel />}

      {/* Party Classification Audit — superadmin only */}
      {user?.role === "superadmin" && <PartyAuditPanel />}

      {/* Display Time Range — all authenticated users */}
      <DisplayTimePanel />

      {/* Database Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-4 h-4" /> Database Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats ? (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {collections.map(({ key, label, icon: Icon }) => (
                <div key={key} className="text-center p-3 rounded-lg bg-muted/50">
                  <Icon className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-2xl font-mono font-semibold">
                    {(stats[key] || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Loading stats...</p>
          )}
        </CardContent>
      </Card>

      {/* Push to Production — superadmin/user only */}
      {user?.role !== 'viewer' && <PushToProductionPanel />}

      {/* Backup & Restore */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <HardDrive className="w-4 h-4" /> Backup & Restore
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-1">Create Backup</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Download all data as a compressed backup file (~{stats ? Math.round((stats.donations || 0) / 18000) : '?'}MB).
              </p>
              <Button
                onClick={handleBackup}
                disabled={loading}
                className="w-full"
                data-testid="backup-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {loading ? "Creating Backup..." : "Download Backup"}
              </Button>
            </div>

            {user?.role !== 'viewer' && (
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-1">Restore from File</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Upload a backup file (.json.gz or .json) to replace all current data.
              </p>
              <label className="block">
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={restoring}
                  asChild
                >
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    {restoring ? "Restoring..." : "Upload Backup File"}
                    <input
                      type="file"
                      accept=".json,.gz,.json.gz"
                      onChange={handleRestore}
                      className="hidden"
                      data-testid="restore-input"
                    />
                  </span>
                </Button>
              </label>
            </div>
            )}

            {user?.role !== 'viewer' && (
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-1">Pull from Remote</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Enter the remote URL to download and restore data. For large databases, first create a backup on the source server (Settings → Create Backup), then pull here — it will skip backup creation and just download.
              </p>
              <Button
                variant="outline"
                onClick={handleRestoreFromUrl}
                disabled={restoring}
                className="w-full"
                data-testid="restore-url-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {restoring ? "Pulling..." : "Pull from Remote"}
              </Button>
            </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Footer Settings */}
      <FooterSettingsCard isSuperadmin={user?.role === "superadmin"} />
    </div>
  );
}

function FooterSettingsCard({ isSuperadmin }) {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/system/footer`)
      .then((r) => { setText(r.data.text || ""); setSaved(r.data.text || ""); })
      .catch(() => {});
  }, []);

  const dirty = text.trim() !== saved.trim();

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true); setMsg(null);
    try {
      const r = await axios.put(`${API}/system/footer`, { text: text.trim() });
      setSaved(r.data.text);
      setText(r.data.text);
      window.dispatchEvent(new Event("footer-updated"));
      setMsg({ tone: "ok", body: "Footer updated. Reload any open tab to see it." });
    } catch (e) {
      setMsg({ tone: "err", body: e.response?.data?.detail || "Failed to save footer" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-4 h-4" /> Footer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isSuperadmin ? (
          <>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Footer text</label>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={300}
              placeholder="© 2026 FEC Watchdog — Created by Bill Casso"
              data-testid="footer-text-input"
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground font-mono">{text.length}/300</span>
              <Button
                size="sm"
                onClick={save}
                disabled={!dirty || saving}
                data-testid="footer-save-btn"
              >
                {saving ? "Saving…" : dirty ? "Save Footer" : "Saved"}
              </Button>
            </div>
            {msg && (
              <p className={`text-xs ${msg.tone === "ok" ? "text-green-600" : "text-red-600"}`}>{msg.body}</p>
            )}
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Only superadmins can edit the footer text.</p>
        )}
        <div className="p-4 border rounded-lg bg-muted/30 text-center space-y-1" data-testid="footer-preview">
          <p className="text-sm text-muted-foreground">
            {text || saved || "© 2026 FEC Watchdog — Created by Bill Casso"}
            <span className="opacity-60 ml-1">· v{require("../../package.json").version}</span>
          </p>
          <SystemVersionBadge />
        </div>
      </CardContent>
    </Card>
  );
}
